import React, { useEffect, useMemo, useRef, useState } from 'react';

import axios from 'axios';

import { ALLOCATION_EPSILON } from './allocationConstants';

import { formatFigure, formatQtyDisplay as formatQty, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import Modal from 'CommonComponents/Modal';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import SegmentedControl from 'CommonComponents/SegmentedControl';

function roundTo(value, decimals) {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
}

/**
 * Fold the residual (target − Σ values) into the entry with the largest
 * `weightKey`, after rounding each entry's `valueKey` to `decimals`. Mirrors
 * the backend's drift-into-largest reconciliation (reconcileAllocatedValues /
 * normalizeBulkAllocations) so switching basis never leaves a fractional
 * remainder that the Unallocated gate can't reach zero on.
 */
function reconcileToLargest(entries, valueKey, weightKey, target, decimals) {
    const rounded = entries.map((e) => ({ ...e, [valueKey]: roundTo(Number(e[valueKey]) || 0, decimals) }));
    if (rounded.length === 0) return rounded;

    const sum = rounded.reduce((acc, e) => acc + e[valueKey], 0);
    const residual = roundTo(target - sum, decimals);
    const threshold = decimals >= 2 ? 0.005 : 0.05;

    if (Math.abs(residual) >= threshold) {
        let largestIdx = 0;
        let largestWeight = -Infinity;
        rounded.forEach((e, idx) => {
            const weight = Number(e[weightKey]) || 0;
            if (weight > largestWeight) {
                largestWeight = weight;
                largestIdx = idx;
            }
        });
        rounded[largestIdx][valueKey] = roundTo(rounded[largestIdx][valueKey] + residual, decimals);
    }

    return rounded;
}

/**
 * Bulk-edit modal for venue→sport allocation split.
 *
 * Flow:
 *   1. User enters a split — by percentage (must sum to 100), or, when exactly
 *      one selected line is an Expense carrying a declared quantity, by quantity
 *      (must sum to that line's total quantity). The always-visible Unallocated
 *      row must reach 0 before Preview/Apply is enabled.
 *   2. Clicks "Preview" → POST to bulkPreviewUrl, shows per-line before/after.
 *   3. Clicks "Confirm all" → POST to bulkApplyUrl, calls onApplied().
 *
 * Props:
 *   - modal              flowbite modal handle from useModal() — parent shows/hides it
 *   - venueId
 *   - sports             [{id, name}, ...]  the venue's sports
 *   - selectedLines      [{type, id, name, qty?, is_expense?, is_manual_override?}, ...]  lines the user has selected
 *   - bulkPreviewUrl     URL with __VENUE_ID__ placeholder
 *   - bulkApplyUrl       URL with __VENUE_ID__ placeholder
 *   - onApplied()
 */
export default function BulkSportAllocationModal({ modal, venueId, sports = [], selectedLines = [], bulkPreviewUrl, bulkApplyUrl, onApplied }) {
    // Quantity mode is available only for a single Expense line that declares a
    // unit quantity — mirrors the backend's single-Expense-line gate.
    const qtyLine = selectedLines.length === 1 ? selectedLines[0] : null;
    const qtyEligible = !!qtyLine && qtyLine.is_expense === true && qtyLine.qty != null && Number(qtyLine.qty) > 0;
    const totalQty = qtyEligible ? Number(qtyLine.qty) : 0;

    const initialRows = useMemo(() => {
        const count = sports.length || 1;
        const evenly = Number((100 / count).toFixed(2));
        return sports.map((s, idx) => ({
            sport_id: s.id,
            sport_name: s.name,
            percentage: idx === sports.length - 1 ? Number((100 - evenly * (count - 1)).toFixed(2)) : evenly,
        }));
    }, [sports]);

    // Even split of the line's total quantity (1-decimal), drift absorbed by the
    // last entry so the quantities sum exactly to the declared total.
    const initialQtyRows = useMemo(() => {
        const count = sports.length || 1;
        if (!qtyEligible) {
            return sports.map((s) => ({ sport_id: s.id, sport_name: s.name, quantity: '' }));
        }
        const base = Number((totalQty / count).toFixed(1));
        return sports.map((s, idx) => ({
            sport_id: s.id,
            sport_name: s.name,
            // The last entry carries the EXACT remainder (to 2dp, not 1dp) so the
            // even split sums to the declared total even when it has >1 decimal
            // place — otherwise Σqty would fall short and Apply could never reach
            // the fully-allocated (Unallocated ≈ 0) gate.
            quantity: idx === sports.length - 1 ? Math.round((totalQty - base * (count - 1)) * 100) / 100 : base,
        }));
    }, [sports, qtyEligible, totalQty]);

    const [mode, setMode] = useState('percentage'); // percentage | quantity
    const [rows, setRows] = useState(initialRows);
    const [qtyRows, setQtyRows] = useState(initialQtyRows);
    const [preview, setPreview] = useState(null);
    const [phase, setPhase] = useState('edit'); // edit | previewing | preview-ready | applying | applied-with-failures | error
    const [error, setError] = useState(null);
    const [applyFailures, setApplyFailures] = useState([]);

    // Reset to a fresh even split every time the modal is opened.
    useEffect(() => {
        if (modal.visible) {
            setMode('percentage');
            setRows(initialRows);
            setQtyRows(initialQtyRows);
            setPreview(null);
            setPhase('edit');
            setError(null);
            setApplyFailures([]);
        }
    }, [modal.visible]);

    // Flowbite appends its backdrop outside the React tree, so unmounting while
    // the modal is still shown (e.g. the table refetches after applying and the
    // bulk controls disappear) would leave the overlay behind — force-hide on unmount.
    const modalStateRef = useRef(modal);
    modalStateRef.current = modal;

    useEffect(
        () => () => {
            const { element } = modalStateRef.current;
            if (element && element.isVisible()) {
                element.hide();
            }
        },
        [],
    );

    // Fall back to percentage whenever quantity mode is not offered, so a stale
    // mode can never leak a quantity payload for an ineligible selection.
    const effectiveMode = qtyEligible ? mode : 'percentage';
    const isQuantityMode = effectiveMode === 'quantity';

    const percentageTotal = useMemo(() => rows.reduce((sum, r) => sum + Number(r.percentage || 0), 0), [rows]);
    const quantityTotal = useMemo(() => qtyRows.reduce((sum, r) => sum + Number(r.quantity || 0), 0), [qtyRows]);

    // Remaining to allocate — 100 − Σ% or totalQty − Σqty. Preview/Apply stay
    // disabled until this reaches (approximately) zero.
    const unallocated = isQuantityMode ? totalQty - quantityTotal : 100 - percentageTotal;
    const isFullyAllocated = Math.abs(unallocated) < ALLOCATION_EPSILON;

    const qtyFigure = (value) => formatFigure(value, formatQty, { precision: 1 });
    const percentFigure = (value) => formatFigure(value, (figure, precision) => Number(figure).toFixed(precision), { precision: 2, suffix: '%' });

    // Warn if any of the selected lines already has a manual override — applying
    // the bulk split will clobber the user's previously-set custom allocation.
    const hasManualOverrideLines = useMemo(() => selectedLines.some((l) => l.is_manual_override === true), [selectedLines]);

    const close = () => modal.hide();

    const updatePercentage = (sportId, value) => {
        setRows((prev) => prev.map((r) => (r.sport_id === sportId ? { ...r, percentage: value === '' ? '' : Number(value) } : r)));
    };

    const updateQuantity = (sportId, value) => {
        setQtyRows((prev) => prev.map((r) => (r.sport_id === sportId ? { ...r, quantity: value === '' ? '' : Number(value) } : r)));
    };

    // Switching % ⇄ quantity must recompute the target basis from whichever
    // basis the user is leaving, so the two stay consistent (per the ticket:
    // "switching recomputes from one canonical basis so the two stay
    // consistent") — rows/qtyRows are otherwise two independent state slices
    // that would silently discard whatever was just typed.
    const handleModeChange = (newMode) => {
        if (newMode === effectiveMode) {
            setMode(newMode);
            return;
        }

        if (newMode === 'quantity' && qtyEligible) {
            const percentageBySportId = new Map(rows.map((r) => [r.sport_id, Number(r.percentage) || 0]));
            const derived = qtyRows.map((qr) => {
                const percentage = percentageBySportId.get(qr.sport_id) || 0;
                return {
                    sport_id: qr.sport_id,
                    sport_name: qr.sport_name,
                    quantity: (totalQty * percentage) / 100,
                    _weight: percentage,
                };
            });
            const reconciled = reconcileToLargest(derived, 'quantity', '_weight', totalQty, 1);
            setQtyRows(reconciled.map(({ sport_id, sport_name, quantity }) => ({ sport_id, sport_name, quantity })));
        } else if (newMode === 'percentage') {
            const quantityBySportId = new Map(qtyRows.map((r) => [r.sport_id, Number(r.quantity) || 0]));
            const derived = rows.map((r) => {
                const quantity = quantityBySportId.get(r.sport_id) || 0;
                return {
                    sport_id: r.sport_id,
                    sport_name: r.sport_name,
                    percentage: totalQty > 0 ? (quantity / totalQty) * 100 : 0,
                    _weight: quantity,
                };
            });
            const reconciled = reconcileToLargest(derived, 'percentage', '_weight', 100, 2);
            setRows(reconciled.map(({ sport_id, sport_name, percentage }) => ({ sport_id, sport_name, percentage })));
        }

        setMode(newMode);
    };

    const buildPayload = () => ({
        lines: selectedLines.map((l) => ({ type: l.type, id: l.id })),
        allocations: isQuantityMode
            ? qtyRows.filter((r) => r.quantity !== '' && Number(r.quantity) > 0).map((r) => ({ sport_id: r.sport_id, quantity: Number(r.quantity) }))
            : rows.filter((r) => r.percentage !== '' && Number(r.percentage) > 0).map((r) => ({ sport_id: r.sport_id, percentage: Number(r.percentage) })),
    });

    const handlePreview = async () => {
        setError(null);
        setPhase('previewing');
        try {
            const url = (bulkPreviewUrl || '').replace('__VENUE_ID__', String(venueId));
            const res = await axios.post(url, buildPayload());
            setPreview(res.data?.data || null);
            setPhase('preview-ready');
        } catch (e) {
            setError(extractError(e));
            setPhase('edit');
        }
    };

    const handleApply = async () => {
        setError(null);
        setApplyFailures([]);
        setPhase('applying');
        try {
            const url = (bulkApplyUrl || '').replace('__VENUE_ID__', String(venueId));
            const res = await axios.post(url, buildPayload());
            const failed = res.data?.data?.failed ?? [];
            if (failed.length > 0) {
                // Some lines failed — surface them before closing so the user
                // knows which lines were not updated. onApplied() is still called
                // so the parent table can refresh the lines that did succeed.
                setApplyFailures(failed);
                setPhase('applied-with-failures');
                if (typeof onApplied === 'function') onApplied();
            } else {
                // Hide before notifying the parent — onApplied triggers a table
                // refetch that can unmount this modal while it is still shown.
                close();
                if (typeof onApplied === 'function') onApplied();
            }
        } catch (e) {
            setError(extractError(e));
            setPhase('preview-ready');
        }
    };

    const isEditPhase = phase === 'edit' || phase === 'previewing';

    return (
        <Modal id="bulk-sport-allocation-modal" modal={modal}>
            <ModalHeader title={$t('Bulk apply event split')} close={close} />

            <div className="modal__form-container">
                {phase !== 'applied-with-failures' && (
                    <p className="modal__text">
                        {selectedLines.length} {$t('lines selected')}
                    </p>
                )}

                {hasManualOverrideLines && phase !== 'applied-with-failures' && (
                    <Alert type="warning">
                        {$t('One or more selected lines already have a custom allocation. Applying the bulk split will overwrite those custom values.')}
                    </Alert>
                )}

                {error && <Alert type="error">{error}</Alert>}

                {phase === 'applied-with-failures' && applyFailures.length > 0 && (
                    <Alert type="warning">
                        <p className="tw-font-semibold tw-mb-1">{$t('{{count}} line(s) could not be updated:', { count: applyFailures.length })}</p>
                        <ul className="tw-list-disc tw-list-inside tw-space-y-1">
                            {applyFailures.map((f, idx) => (
                                <li key={idx}>
                                    {f.type} #{f.id}: {f.reason}
                                </li>
                            ))}
                        </ul>
                    </Alert>
                )}

                {isEditPhase && (
                    <div>
                        {qtyEligible && (
                            <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between">
                                <span className="modal__text">{$t('Split by')}</span>
                                <SegmentedControl
                                    value={effectiveMode}
                                    onChange={handleModeChange}
                                    options={[
                                        { value: 'percentage', label: $t('Percentage') },
                                        { value: 'quantity', label: $t('Quantity') },
                                    ]}
                                />
                            </div>
                        )}

                        <table className="tw-w-full tw-text-sm">
                            <thead>
                                <tr className="tw-border-b tw-border-grey">
                                    <th className="modal__heading tw-text-start tw-py-2">{$t('Event')}</th>
                                    <th className="modal__heading tw-text-end tw-py-2 tw-w-32">
                                        {isQuantityMode ? $t('Qty (of {{total}})', { total: formatQty(totalQty) }) : '%'}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {isQuantityMode
                                    ? qtyRows.map((r) => (
                                          <SplitRow
                                              key={r.sport_id}
                                              sportName={r.sport_name}
                                              step="0.1"
                                              max={totalQty}
                                              value={r.quantity}
                                              onChange={(e) => updateQuantity(r.sport_id, e.target.value)}
                                          />
                                      ))
                                    : rows.map((r) => (
                                          <SplitRow
                                              key={r.sport_id}
                                              sportName={r.sport_name}
                                              step="0.01"
                                              max="100"
                                              value={r.percentage}
                                              onChange={(e) => updatePercentage(r.sport_id, e.target.value)}
                                              suffix="%"
                                          />
                                      ))}
                                <tr className="tw-font-semibold">
                                    <td className="modal__text tw-py-2 tw-text-end">{$t('Total')}</td>
                                    <td className="tw-py-2 tw-text-end tw-text-charcoal-75">
                                        {isQuantityMode ? qtyFigure(quantityTotal) : percentFigure(percentageTotal)}
                                    </td>
                                </tr>
                                <tr className="tw-font-semibold">
                                    <td className="modal__text tw-py-2 tw-text-end">{$t('Unallocated')}</td>
                                    <td className={`tw-py-2 tw-text-end ${isFullyAllocated ? 'tw-text-green' : 'tw-text-red'}`}>
                                        {isQuantityMode ? qtyFigure(Number(unallocated.toFixed(1))) : percentFigure(Number(unallocated.toFixed(2)))}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        {!isFullyAllocated && (
                            <p className="modal__input-error-text tw-mt-2">
                                {isQuantityMode
                                    ? $t('Quantities must add up to the line total of {{total}}.', { total: formatQty(totalQty) })
                                    : $t('Percentages must sum to 100')}
                            </p>
                        )}
                    </div>
                )}

                {phase === 'preview-ready' && preview && <PreviewTable preview={preview} />}

                {isEditPhase && (
                    <ModalFooter
                        cancelButtonText={$t('Cancel')}
                        primaryButtonText={$t('Preview')}
                        submitButtonVariant="primary"
                        onCancel={close}
                        handleSubmit={handlePreview}
                        isSubmitting={phase === 'previewing'}
                        primaryButtonDisabled={!isFullyAllocated || selectedLines.length === 0}
                    />
                )}

                {(phase === 'preview-ready' || phase === 'applying') && (
                    <div className="modal__footer">
                        <Button variant="secondary" onClick={close} disabled={phase === 'applying'}>
                            {$t('Cancel')}
                        </Button>
                        <Button variant="secondary" onClick={() => setPhase('edit')} disabled={phase === 'applying'}>
                            {$t('Back')}
                        </Button>
                        <Button variant="primary" onClick={handleApply} disabled={phase === 'applying'} loading={phase === 'applying'}>
                            {$t('Confirm all ({{count}} lines)', { count: preview?.lines_affected ?? 0 })}
                        </Button>
                    </div>
                )}

                {phase === 'applied-with-failures' && (
                    <div className="modal__footer">
                        <Button variant="secondary" onClick={close}>
                            {$t('Close')}
                        </Button>
                    </div>
                )}
            </div>
        </Modal>
    );
}

/**
 * One splitter row — a sport name plus its %/quantity input. The % and
 * quantity branches previously differed only in step/max/value/onChange and
 * the trailing '%' suffix; extracted so the table body reads as "which
 * basis", not "two near-identical tables".
 */
function SplitRow({ sportName, step, max, value, onChange, suffix }) {
    return (
        <tr className="tw-border-b tw-border-grey">
            <td className="modal__text tw-py-2">{sportName}</td>
            <td className="tw-py-2">
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-x-1.5">
                    <div className="tw-w-24">
                        <input type="number" step={step} min="0" max={max} value={value} onChange={onChange} className="text-input tw-border-0 tw-text-end" />
                    </div>
                    {suffix && <span className="modal__text">{suffix}</span>}
                </div>
            </td>
        </tr>
    );
}

function PreviewTable({ preview }) {
    return (
        <div className="tw-space-y-2">
            <p className="modal__msg modal__text-color">{$t('Apply this split to {{count}} selected lines?', { count: preview.lines_affected })}</p>
            <div className="tw-max-h-64 tw-overflow-y-auto tw-border tw-border-grey tw-rounded-lg">
                <table className="tw-w-full tw-text-sm">
                    <thead className="tw-bg-charcoal-10 tw-sticky tw-top-0">
                        <tr>
                            <th className="modal__heading tw-text-start tw-px-3 tw-py-2">{$t('Line')}</th>
                            <th className="modal__heading tw-text-start tw-px-3 tw-py-2">{$t('New split')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(preview.per_line || []).map((line) => (
                            <tr key={`${line.type}-${line.id}`} className="tw-border-t tw-border-grey">
                                <td className="modal__text tw-px-3 tw-py-2">{line.name}</td>
                                <td className="tw-px-3 tw-py-2">
                                    {(line.after || []).map((a, idx) => (
                                        <div key={idx} className="tw-text-xs tw-text-charcoal-75">
                                            {a.sport_name}: {a.quantity != null ? `${formatQty(a.quantity)} (${a.percentage}%)` : `${a.percentage}%`} →{' '}
                                            {formatFigure(a.allocated_value, formatToFixedMoneyWithLocale, { precision: 2 })}
                                        </div>
                                    ))}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function extractError(e) {
    if (e?.response?.data?.errors) {
        const first = Object.values(e.response.data.errors)[0];
        if (Array.isArray(first) && first.length) return first[0];
    }
    if (e?.response?.data?.message) return e.response.data.message;
    return e?.message || $t('Something went wrong');
}
